import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HUBSPOT_PORTAL_ID = "143197515";
const HUBSPOT_FORM_ID = "f2b5a2bd-a051-4e61-8fef-9b8effd3bb55";

export const NewsletterBanner = () => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !lastName || !email) return;

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: [
              { name: "firstname", value: firstName },
              { name: "lastname", value: lastName },
              { name: "email", value: email },
            ],
            context: {
              pageUri: window.location.href,
              pageName: document.title,
            },
          }),
        }
      );

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(t("blog.newsletter.error", "Er ging iets mis. Probeer het opnieuw."));
      }
    } catch {
      setError(t("blog.newsletter.error", "Er ging iets mis. Probeer het opnieuw."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-8">
      <div
        className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-stretch bg-secondary"
        style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}
      >
        {/* Left column - Text + Form */}
        <div className="p-8 md:p-12 flex-1 max-w-xl">
          <h3 className="text-2xl md:text-3xl font-bold mb-3 text-foreground">
            {t("blog.newsletter.title", "Ontvang de nieuwste HR-tips")}
          </h3>
          <p className="text-muted-foreground mb-6 text-[15px]">
            {t(
              "blog.newsletter.subtitle",
              "Krijg 1 keer per maand onze tips, e-guides en templates direct in je mail."
            )}
          </p>

          {submitted ? (
            <p className="font-semibold text-foreground">
              {t("blog.newsletter.success", "Bedankt voor je inschrijving! 🎉")}
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium mb-1 block text-foreground">
                    {t("blog.newsletter.firstName", "Voornaam")} <span className="text-primary">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="rounded-full border-none bg-white px-5 py-3 h-11 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1 block text-foreground">
                    {t("blog.newsletter.lastName", "Achternaam")} <span className="text-primary">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="rounded-full border-none bg-white px-5 py-3 h-11 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block text-foreground">
                  {t("blog.newsletter.email", "E-mail")} <span className="text-primary">*</span>
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-full border-none bg-white px-5 py-3 h-11 text-sm"
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="rounded-full px-8 py-3 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {submitting
                  ? "..."
                  : t("blog.newsletter.submit", "Ik schrijf me in")}
              </button>
            </form>
          )}
        </div>

        {/* Right column - Image */}
        <div className="hidden md:flex flex-1 items-center justify-center p-6">
          <div className="w-80 h-60 rounded-2xl overflow-hidden">
            <img
              src="/images/blog/newsletter-banner-person.png"
              alt="HR professional"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
